import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { RestResponse } from '../models/restResponse.mode';
import type {
  ThermalCashDrawerCloseDto,
  ThermalCashDrawerOpenDto,
  ThermalCrediarioInstallmentsDto,
  ThermalSaleReceiptDto
} from '../models/thermal-receipt.model';

export interface ThermalPrintOptions {
  /** Se true, abre janela com prévia e botão Imprimir antes do diálogo do sistema. */
  preview?: boolean;
  /**
   * Largura aproximada da bobina em mm (sugestão para @page e largura do texto).
   * Padrão 72.
   */
  paperWidthMm?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ThermalPrintService {
  private readonly baseUrl = `${environment.base_url}/pdv/thermal-print`;

  constructor(private http: HttpClient) {}

  printSaleReceipt(saleId: number, options?: ThermalPrintOptions): void {
    this.fetchSaleReceipt(saleId).subscribe({
      next: (dto) => this.renderAndPrint(this.buildSaleReceiptHtml(dto), options),
      error: (err) => console.error('Erro ao carregar cupom de venda:', err)
    });
  }

  printCashOpen(pdvId: number, options?: ThermalPrintOptions): void {
    this.fetchCashOpen(pdvId).subscribe({
      next: (dto) => this.renderAndPrint(this.buildCashOpenHtml(dto), options),
      error: (err) => console.error('Abertura de caixa:', err)
    });
  }

  printCashClose(pdvId: number, options?: ThermalPrintOptions): void {
    this.fetchCashClose(pdvId).subscribe({
      next: (dto) => this.renderAndPrint(this.buildCashCloseHtml(dto), options),
      error: (err) => console.error('Fechamento de caixa:', err)
    });
  }

  printCrediarioInstallments(saleId: number, options?: ThermalPrintOptions): void {
    this.fetchCrediario(saleId).subscribe({
      next: (dto) => this.renderAndPrint(this.buildCrediarioHtml(dto), options),
      error: (err) => console.error('Parcelas crediário:', err)
    });
  }

  fetchSaleReceipt(saleId: number): Observable<ThermalSaleReceiptDto> {
    return this.http
      .get<RestResponse<ThermalSaleReceiptDto>>(`${this.baseUrl}/sale-receipt/${saleId}`)
      .pipe(map((r) => r.data));
  }

  private fetchCashOpen(pdvId: number): Observable<ThermalCashDrawerOpenDto> {
    return this.http
      .get<RestResponse<ThermalCashDrawerOpenDto>>(`${this.baseUrl}/cash-open/${pdvId}`)
      .pipe(map((r) => r.data));
  }

  private fetchCashClose(pdvId: number): Observable<ThermalCashDrawerCloseDto> {
    return this.http
      .get<RestResponse<ThermalCashDrawerCloseDto>>(`${this.baseUrl}/cash-close/${pdvId}`)
      .pipe(map((r) => r.data));
  }

  private fetchCrediario(saleId: number): Observable<ThermalCrediarioInstallmentsDto> {
    return this.http
      .get<RestResponse<ThermalCrediarioInstallmentsDto>>(
        `${this.baseUrl}/crediario-installments/${saleId}`
      )
      .pipe(map((r) => r.data));
  }

  private static readonly DefaultThermalWidthMm = 72;

  private thermalStyles(widthMm: number): string {
    const w = widthMm;
    return `
    <style>
      @page { size: ${w}mm auto; margin: 2mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 4mm;
        font-family: 'Courier New', 'Consolas', monospace;
        font-size: 11px;
        line-height: 1.35;
        color: #000;
        max-width: ${w}mm;
      }
      @media print {
        body { padding: 2mm; }
        .no-print { display: none !important; }
      }
      .c { text-align: center; }
      .b { font-weight: bold; }
      .sep { border-top: 1px dashed #000; margin: 6px 0; padding-top: 6px; }
      pre.line { margin: 0; white-space: pre-wrap; font-family: inherit; font-size: inherit; }
      table.items { width: 100%; border-collapse: collapse; }
      table.items td { padding: 2px 0; vertical-align: top; }
      .btn-print {
        margin: 12px auto;
        display: block;
        padding: 10px 20px;
        font-size: 14px;
        cursor: pointer;
      }
    </style>`;
  }

  private renderAndPrint(htmlBody: string, options?: ThermalPrintOptions): void {
    const preview = options?.preview === true;
    const widthMm = options?.paperWidthMm ?? ThermalPrintService.DefaultThermalWidthMm;
    const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"/>${this.thermalStyles(widthMm)}</head><body>${htmlBody}</body></html>`;

    if (preview) {
      const w = window.open('', '_blank', 'width=420,height=640,scrollbars=yes');
      if (!w) {
        alert('Permita pop-ups para ver a prévia do cupom.');
        return;
      }
      w.document.open();
      w.document.write(docHtml);
      w.document.close();
      const btn = w.document.createElement('button');
      btn.className = 'btn-print no-print';
      btn.textContent = 'Imprimir';
      btn.onclick = () => {
        w.focus();
        w.print();
      };
      w.document.body.appendChild(btn);
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'impressao-termica');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;visibility:hidden';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(docHtml);
    doc.close();

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch {
        /* ignore */
      }
    };

    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } finally {
        setTimeout(cleanup, 800);
      }
    }, 150);
  }

  private buildSaleReceiptHtml(d: ThermalSaleReceiptDto): string {
    const esc = (s: string) => this.escapeHtml(s ?? '');
    const money = (n: number) =>
      (typeof n === 'number' ? n : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const dt = d.saleDate ? new Date(d.saleDate) : new Date();

    const lines = (d.items ?? []).map((i) => {
      const desc = esc(i.description);
      const q = money(i.quantity);
      const un = money(i.unitPrice);
      const tot = money(i.lineTotal);
      return `<div class="sep"></div><pre class="line">${desc}</pre><pre class="line">  ${q} x ${un} = ${tot}</pre>`;
    });

    return `
      <div class="c b">${esc(d.storeName)}</div>
      <div class="c">${esc(d.cnpj)}</div>
      <div class="c">${esc(d.phone)}</div>
      <div class="c">${esc(d.addressLine1)}</div>
      <div class="c">${esc(d.addressLine2)}</div>
      <div class="c b" style="margin-top:8px">CUPOM DE VENDA</div>
      <div class="sep"></div>
      <pre class="line">${esc(d.saleNumber)}</pre>
      <pre class="line">Data: ${dt.toLocaleString('pt-BR')}</pre>
      <pre class="line">${esc(d.cashier)}</pre>
      <div class="sep"></div>
      <pre class="line b">ITENS</pre>
      ${lines.join('')}
      <div class="sep"></div>
      <pre class="line">Pagamentos: ${esc(d.paymentSummary)}</pre>
      <pre class="line">Desconto: ${money(d.discount)}</pre>
      <pre class="line b">Total: ${money(d.netTotal)}</pre>
      <pre class="line">Valor pago: ${money(d.amountPaid)}</pre>
      <pre class="line">Troco: ${money(d.change)}</pre>
      <div class="sep"></div>
      <div class="c b">SEM VALOR FISCAL</div>
    `;
  }

  private buildCashOpenHtml(d: ThermalCashDrawerOpenDto): string {
    const esc = (s: string) => this.escapeHtml(s ?? '');
    const money = (n: number) =>
      (typeof n === 'number' ? n : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const dt = d.openingTime ? new Date(d.openingTime) : new Date();

    return `
      <div class="c b">${esc(d.storeName)}</div>
      <div class="c">${esc(d.cnpj)}</div>
      <div class="c">${esc(d.phone)}</div>
      <div class="c">${esc(d.addressLine1)}</div>
      <div class="c">${esc(d.addressLine2)}</div>
      <div class="c b" style="margin-top:8px">ABERTURA DE CAIXA</div>
      <div class="sep"></div>
      <pre class="line">${esc(d.shiftLabel)}</pre>
      <pre class="line">Data/Hora: ${dt.toLocaleString('pt-BR')}</pre>
      <pre class="line">Operador: ${esc(d.operatorName)}</pre>
      <pre class="line b">Valor abertura: ${money(d.openingAmount)}</pre>
      <div class="sep"></div>
      <div class="c b">SEM VALOR FISCAL</div>
    `;
  }

  private buildCashCloseHtml(d: ThermalCashDrawerCloseDto): string {
    const esc = (s: string) => this.escapeHtml(s ?? '');
    const money = (n: number) =>
      (typeof n === 'number' ? n : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const tSold =
      d.totalSoldCash +
      d.totalSoldPix +
      d.totalSoldDebit +
      d.totalSoldCredit +
      d.totalSoldCrediario +
      d.totalSoldCheque +
      d.totalSoldTransfer +
      d.totalSoldCashback;
    const totalRemaining = d.openingAmount + d.entryAmount + tSold - d.withdrawalAmount;

    return `
      <div class="c b">${esc(d.storeName)}</div>
      <div class="c">${esc(d.cnpj)}</div>
      <div class="c">${esc(d.phone)}</div>
      <div class="c">${esc(d.addressLine1)}</div>
      <div class="c">${esc(d.addressLine2)}</div>
      <div class="c b" style="margin-top:8px">FECHAMENTO DE CAIXA</div>
      <div class="sep"></div>
      <pre class="line">${esc(d.shiftLabel)}</pre>
      <pre class="line">Abertura: ${new Date(d.openingTime).toLocaleString('pt-BR')}</pre>
      <pre class="line">Fechamento: ${new Date(d.closingTime).toLocaleString('pt-BR')}</pre>
      <pre class="line">Operador: ${esc(d.operatorName)}</pre>
      <div class="sep"></div>
      <pre class="line">Total declarado: ${money(d.totalDeclared)}</pre>
      <pre class="line">Total calculado: ${money(d.totalCalculated)}</pre>
      <pre class="line">Diferença: ${money(d.totalDifference)}</pre>
      <div class="sep"></div>
      <pre class="line b">Vendido por forma de pagamento</pre>
      <pre class="line">Dinheiro: ${money(d.totalSoldCash)}</pre>
      <pre class="line">Pix: ${money(d.totalSoldPix)}</pre>
      <pre class="line">Débito: ${money(d.totalSoldDebit)}</pre>
      <pre class="line">Crédito: ${money(d.totalSoldCredit)}</pre>
      <pre class="line">Crediário: ${money(d.totalSoldCrediario)}</pre>
      <pre class="line">Cheque: ${money(d.totalSoldCheque)}</pre>
      <pre class="line">TED/DOC: ${money(d.totalSoldTransfer)}</pre>
      <pre class="line">Cashback: ${money(d.totalSoldCashback)}</pre>
      <pre class="line b">Total vendido: ${money(tSold)}</pre>
      <div class="sep"></div>
      <pre class="line">Valor abertura: ${money(d.openingAmount)}</pre>
      <pre class="line">Entrada (suprimento): ${money(d.entryAmount)}</pre>
      <pre class="line">Retirada (sangria): ${money(d.withdrawalAmount)}</pre>
      <pre class="line b">Total restante: ${money(totalRemaining)}</pre>
      <div class="sep"></div>
      <div class="c b">SEM VALOR FISCAL</div>
    `;
  }

  private buildCrediarioHtml(d: ThermalCrediarioInstallmentsDto): string {
    const esc = (s: string) => this.escapeHtml(s ?? '');
    const money = (n: number) =>
      (typeof n === 'number' ? n : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const rows = (d.parcelas ?? [])
      .map(
        (p) =>
          `<pre class="line">${p.numero}º  venc. ${new Date(p.vencimento).toLocaleDateString('pt-BR')}  ${money(p.valor)}</pre>`
      )
      .join('');

    return `
      <div class="c b">${esc(d.storeName)}</div>
      <div class="c">${esc(d.cnpj)}</div>
      <div class="c">${esc(d.phone)}</div>
      <div class="c">${esc(d.addressLine1)}</div>
      <div class="c">${esc(d.addressLine2)}</div>
      <div class="c b" style="margin-top:8px">CREDIÁRIO — PARCELAS</div>
      <div class="sep"></div>
      <pre class="line">${esc(d.saleNumber)}</pre>
      <pre class="line">Data venda: ${new Date(d.saleDate).toLocaleString('pt-BR')}</pre>
      <pre class="line">Cliente: ${esc(d.customerName)}</pre>
      <pre class="line">Doc: ${esc(d.customerDocument)}</pre>
      <pre class="line">Entrada: ${money(d.entrada)}</pre>
      <pre class="line b">Financiado: ${money(d.totalFinanciado)}</pre>
      <pre class="line">Parcelas: ${d.quantidadeParcelas}</pre>
      <div class="sep"></div>
      ${rows}
      <div class="sep"></div>
      <div class="c b">SEM VALOR FISCAL</div>
    `;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
